/**
 * safetyNetLane — pure derivations behind the Safety net lane (the left-
 * behind-leads surface, docs/crm-safety-net-plan.md §5). The page renders
 * EXACTLY what these functions return; keeping them React-free makes the
 * evidence lines and filter math directly testable (the needsYou.ts pattern).
 * Named safetyNetLane, NOT safetyNet: on a case-insensitive filesystem a
 * sibling `safetyNet.ts` shadows `SafetyNet.tsx` in the pages barrel resolve.
 */
import type { ContactListItem } from '../types';

/** One open catch on a lead — GET /api/crm/safety-net `items[].catches[]`. */
export interface SafetyNetCatch {
  id: string;
  detector: string;
  first_caught_at: string;
  last_seen_at: string;
  evidence: Record<string, any>;
  /** Set = reviewed and parked until the lead's state moves (plan §4a). */
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  acknowledge_note?: string | null;
  /** The backstop: it returns to the lane on its own at this moment. */
  ack_expires_at?: string | null;
}

export interface SafetyNetItem {
  contact: ContactListItem;
  catches: SafetyNetCatch[];
}

export interface SafetyNetResponse {
  generated_at: string;
  total: number;
  by_detector: Record<string, number>;
  /** Leads currently parked as reviewed — the "Reviewed" view's size. */
  acknowledged_total?: number;
  view?: 'active' | 'acknowledged';
  items?: SafetyNetItem[];
}

/** Chip label per detector — short, recruiter-facing, no jargon. */
export const DETECTOR_LABEL: Record<string, string> = {
  ghosted_engaged: 'Ghosted after engaging',
  reply_owed: 'Reply owed',
  assigned_idle: 'Assigned but idle',
  stage_sla: 'Stuck in stage',
  missed_callback: 'Missed callback',
  stuck_review: 'Review stuck',
  stalled_conversation: 'Agent stalled',
  // Out-of-office coverage's own backstop: the owner is away AND the cover pool
  // resolved empty, so this lead is genuinely nobody's today.
  owner_absent_uncovered: 'Owner out, uncovered',
};

/** Filter-chip order: hottest first, matching the lane's triage instinct. */
export const DETECTOR_ORDER = [
  // Beside assigned_idle: same hole (an owned lead nobody is working), but
  // named at the moment it opens rather than inferred from days of silence —
  // so it outranks it.
  'reply_owed', 'ghosted_engaged', 'missed_callback', 'stalled_conversation',
  'owner_absent_uncovered', 'assigned_idle', 'stage_sla', 'stuck_review',
];

const DAY_MS = 86400e3;

const days = (iso: string | null | undefined, now: Date): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : Math.max(0, Math.floor((now.getTime() - ms) / DAY_MS));
};

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/**
 * The one-line evidence sentence under a catch chip — why this lead is here,
 * in the recruiter's terms, from the sweep's point-in-time evidence snapshot.
 */
export function evidenceLine(c: SafetyNetCatch, now: Date = new Date()): string {
  const e = c.evidence || {};
  switch (c.detector) {
    case 'ghosted_engaged': {
      const parts: string[] = [];
      if (typeof e.days_quiet === 'number') parts.push(`quiet ${plural(e.days_quiet, 'day')}`);
      const overdue = days(e.overdue_since, now);
      if (overdue != null) parts.push(`follow-up due ${plural(overdue, 'day')} ago`);
      if (e.origin === 'agent_dormant') parts.push('agent gave up and handed back');
      if (e.sms_opt_out) parts.push('texting opted out — call instead');
      return parts.join(' · ') || 'engaged lead with no follow-up scheduled';
    }
    case 'reply_owed':
      return typeof e.hours_waiting === 'number'
        ? `their message has waited ${plural(e.hours_waiting, 'hour')}${e.agent_active ? ' — agent active but silent' : ''}`
        : 'an inbound message is waiting on a reply';
    case 'assigned_idle':
      return typeof e.days_idle === 'number'
        ? `no activity for ${plural(e.days_idle, 'day')} while assigned`
        : 'assigned with no recent activity';
    case 'stage_sla':
      return typeof e.days_in_stage === 'number'
        ? `${plural(e.days_in_stage, 'day')} in stage (cap ${e.sla_days ?? '?'}d)`
        : 'in stage past its dwell cap';
    case 'missed_callback': {
      const d = days(e.scheduled_at, now);
      return d != null
        ? `booked call missed ${plural(d, 'day')} ago, never rebooked`
        : 'a booked call was missed and never rebooked';
    }
    case 'stuck_review':
      return e.review_status
        ? `review stuck in "${String(e.review_status).replace(/_/g, ' ')}"${typeof e.minutes_stuck === 'number' ? ` for ${e.minutes_stuck}m` : ''} — re-run it`
        : 'an eligibility review never finished';
    case 'stalled_conversation':
      return e.conversation_status === 'handed_off'
        ? 'handed to a human, nobody picked it up'
        : `agent paused${e.pause_reason ? ` (${e.pause_reason})` : ''} and never resumed`;
    case 'owner_absent_uncovered': {
      // The evidence the detector writes: due_at, due_reason
      // ('reply_owed' | 'next_action_due'), within_hours, absence_ends_on?.
      const what = e.due_reason === 'reply_owed' ? 'a reply is owed' : 'work is due';
      const back = e.absence_ends_on
        ? ` (back ${String(e.absence_ends_on).slice(5).replace('-', '/')})`
        : '';
      return `owner is out${back} and nobody is covering — ${what}`;
    }
    default:
      return 'left behind';
  }
}

/**
 * What a parked (acknowledged) row says about itself: who parked it, when, and
 * when it comes back on its own. Deliberately explicit about the return date —
 * an acknowledgement the recruiter thinks is permanent is the failure mode this
 * whole surface exists to prevent.
 */
export function ackLine(
  c: SafetyNetCatch,
  nameOf: (id: string) => string | undefined,
  now: Date = new Date(),
): string {
  if (!c.acknowledged_at) return '';
  const who = c.acknowledged_by ? nameOf(c.acknowledged_by) : null;
  const bits = [`Reviewed${who ? ` by ${who}` : ''}`];
  if (c.ack_expires_at) {
    const ms = new Date(c.ack_expires_at).getTime();
    if (!Number.isNaN(ms)) {
      const days = Math.max(0, Math.round((ms - now.getTime()) / 86400e3));
      bits.push(days === 0 ? 'back on the list today' : `back in ${days} day${days === 1 ? '' : 's'} if nothing changes`);
    }
  }
  if (c.acknowledge_note) bits.push(`“${c.acknowledge_note}”`);
  return bits.join(' · ');
}

/** Items narrowed to one detector ('' = all), preserving server order. */
export function filterItems(items: SafetyNetItem[], detector: string): SafetyNetItem[] {
  if (!detector) return items;
  return items.filter((it) => it.catches.some((c) => c.detector === detector));
}

/**
 * Chip counts for the filter bar. Uses by_detector (catch counts) so a chip
 * says how many CATCHES of that kind exist; the header total stays the deduped
 * lead count — the two disagree on purpose when one lead holds two catches.
 */
export function detectorChips(byDetector: Record<string, number>): { key: string; label: string; count: number }[] {
  return DETECTOR_ORDER
    .map((key) => ({ key, label: DETECTOR_LABEL[key] || key, count: byDetector[key] || 0 }))
    .filter((c) => c.count > 0);
}
